import gql from "graphql-tag";

import type {GqlFetchFn} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import {extractProgram} from "../internal/extractors.ts";

import type {
    userSpinoffsOfProgram,
    userSpinoffsOfProgramVariables,
} from "./__graphql-types__/userSpinoffsOfProgram.ts";
import type {
    FetchParams,
    ProjectListFetcher,
    ProjectListResponse,
} from "./project-list-fetcher-types.ts";

export type GqlSpinoffOriginKind =
    | "CHALLENGE"
    | "INTERACTIVE"
    | "PROJECT"
    | "TALKTHROUGH"
    | "PROGRAM";

export type GqlSpinoffOrigin = {
    kind: GqlSpinoffOriginKind;
    programOrContentId: string;
};

type ConstructorParams = {
    gqlFetch: GqlFetchFn;
    spinoffOrigin: GqlSpinoffOrigin;
    itemsPerPage: number;
};

const USER_SPINOFFS_OF_PROGRAM_QUERY: GqlOperation<
    userSpinoffsOfProgram,
    userSpinoffsOfProgramVariables
> = gqlOp(gql`
    query userSpinoffsOfProgram(
        $spinoffOrigin: SpinoffOrigin!
        $pageInfo: ListProgramsPageInfo
        $sort: ListProgramSortOrder
    ) {
        user {
            id
            programSpinoffs(
                from: $spinoffOrigin
                pageInfo: $pageInfo
                sort: $sort
            ) {
                complete
                cursor
                programs {
                    id
                    key
                    authorKaid
                    authorNickname
                    displayableSpinoffCount
                    imagePath
                    sumVotesIncremented
                    translatedTitle: title
                    url
                }
            }
        }
    }
`);

export class UserSpinoffsOfProgramFetcher implements ProjectListFetcher {
    gqlFetch: GqlFetchFn;
    spinoffOrigin: GqlSpinoffOrigin;
    itemsPerPage: number;

    constructor(params: ConstructorParams) {
        this.gqlFetch = params.gqlFetch;
        this.spinoffOrigin = params.spinoffOrigin;
        this.itemsPerPage = params.itemsPerPage;
    }

    async fetch(params: FetchParams): Promise<ProjectListResponse> {
        const response = await this.gqlFetch(USER_SPINOFFS_OF_PROGRAM_QUERY, {
            variables: {
                spinoffOrigin: this.spinoffOrigin,
                sort: params.sort,
                pageInfo: {
                    cursor: params.cursor,
                    itemsPerPage: this.itemsPerPage,
                },
            },
        });

        const page = response.user?.programSpinoffs ?? {
            complete: true,
            cursor: "",
            programs: [],
        };
        return {
            complete: page.complete,
            cursor: page.cursor,
            scratchpads: page.programs.map(extractProgram),
        };
    }

    fingerprint(): string {
        return `UserSpinoffsOfProgramFetcher ${this.spinoffOrigin.kind}:${this.spinoffOrigin.programOrContentId}`;
    }
}
